<script setup>
import { computed, inject } from 'vue';
import { initials, styleVars } from '../Trailhead/shared.js';
import { socialLinks } from '../socialIcons.js';
import { footerColumns } from '../menuLinks.js';

// The footer as the Hub draws it: the hub page's quiet footer — a rule,
// then the org and its link columns on the page ground, and the hub's
// "Powered by TrailsIQ" line. Same config as ThFooter and ByFooter.
const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});
const editing = inject('tiqEditorCanvas', false);

// Less any link to the Trails page while it has nothing to list (menuLinks.js).
const columns = computed(() => footerColumns(props.config.columns, props.site_data, editing));
const social = computed(() => socialLinks(props.config, props.site_data));
const mark = computed(() => initials(props.org?.name));
// The optional line over the icons ("Follow us on"), shown only while
// there is an icon for it to sit over. Same key ThFooter reads.
const eyebrow = computed(() => String(props.config.social_eyebrow ?? '').trim());
const copyright = computed(() => props.config.copyright || `© ${new Date().getFullYear()} ${props.org?.name || ''}`.trim());
</script>

<template>
    <footer :id="config.anchor_id || null" class="th-block hub-footer" :style="styleVars(config)">
        <div class="th-wrap">
            <div class="hub-footer__cols">
                <div class="hub-footer__brand">
                    <div class="hub-footer__id">
                        <span class="hub-footer__logo" :class="{ 'hub-footer__logo--img': org.logo_url }">
                            <img v-if="org.logo_url" :src="org.logo_url" alt="">
                            <span v-else>{{ mark }}</span>
                        </span>
                        <span class="hub-footer__name">{{ org.name }}</span>
                    </div>
                    <p v-if="config.tagline" class="hub-footer__tagline">{{ config.tagline }}</p>
                    <p v-if="eyebrow && social.length" class="hub-footer__head hub-footer__social-eyebrow" data-footer-social-eyebrow>{{ eyebrow }}</p>
                    <div v-if="social.length" class="hub-footer__social">
                        <a v-for="(s, i) in social" :key="i" :href="s.href"
                           :target="s.newTab ? '_blank' : null" rel="noopener"
                           :aria-label="s.label" class="hub-footer__social-link">
                            <img v-if="s.img" :src="s.img" alt="" class="tiq-social-go">
                            <svg v-else viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path :d="s.d" /></svg>
                        </a>
                    </div>
                </div>
                <div v-for="(col, ci) in columns" :key="ci" class="hub-footer__col">
                    <p v-if="col.heading" class="hub-footer__head">{{ col.heading }}</p>
                    <a v-for="(link, li) in (col.links || [])" :key="li" :href="link.href || '#'" class="hub-footer__link">{{ link.label }}</a>
                </div>
            </div>
            <div class="hub-footer__bottom">
                <span>{{ copyright }}</span>
                <a href="https://trailsiq.com" target="_blank" rel="noopener" class="hub-footer__powered">Powered by <strong>Trails<span>IQ</span></strong></a>
            </div>
        </div>
    </footer>
</template>

<style scoped>
.hub-footer { background: var(--block-bg, transparent); color: var(--block-text, #101a15); padding-bottom: calc(48px + env(safe-area-inset-bottom)); }
.hub-footer__cols {
    border-top: 1px solid rgba(16, 26, 21, .1);
    padding-top: 28px;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
    gap: 24px;
}
.hub-footer__brand { display: flex; flex-direction: column; gap: 10px; }
.hub-footer__id { display: flex; align-items: center; gap: 10px; }
.hub-footer__logo {
    width: 32px; height: 32px; border-radius: 9px; overflow: hidden; flex: none;
    background: var(--hub-head, #0e2419); color: var(--hub-head-ink, #eaf2ed);
    display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 800;
}
.hub-footer__logo--img { background: #fff; border: 1px solid rgba(16, 26, 21, .07); padding: 3px; }
.hub-footer__logo img { width: 100%; height: 100%; object-fit: contain; }
.hub-footer__name { font-size: 15px; font-weight: 800; letter-spacing: -0.02em; }
.hub-footer__tagline { margin: 0; font-size: 13px; line-height: 1.5; color: var(--text-sub, #5d6b63); }
.hub-footer__social { display: flex; gap: 8px; }
.hub-footer__social-link {
    width: 34px; height: 34px; border-radius: 10px; background: #fff;
    border: 1px solid rgba(16, 26, 21, .12); color: #26332c;
    display: inline-flex; align-items: center; justify-content: center;
}
.hub-footer__social-link:hover { border-color: var(--hub-accent, #8a5a12); color: var(--hub-accent, #8a5a12); }
.hub-footer__social-link svg { width: 15px; height: 15px; }
.tiq-social-go { display: block; width: 20px; height: 20px; object-fit: contain; }
.hub-footer__col { display: flex; flex-direction: column; gap: 8px; }
.hub-footer__head {
    margin: 0; font-size: 10.5px; font-weight: 800; letter-spacing: .09em;
    text-transform: uppercase; color: #5d6b63;
}
.hub-footer__link { font-size: 13.5px; font-weight: 700; color: #26332c; }
.hub-footer__link:hover { color: var(--hub-accent, #8a5a12); }
.hub-footer__bottom {
    margin-top: 28px;
    display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 10px;
    font-size: 12.5px; color: #7c8a82;
}
.hub-footer__powered { color: inherit; }
.hub-footer__powered strong { color: #101a15; }
.hub-footer__powered strong span { color: var(--hub-accent, #8a5a12); }
</style>
